import { notify } from '../utils/notify';
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { userAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Camera, Pencil, Save } from 'lucide-react';
import './Profile.css';

const Profile = () => {
  const { user, updateUser } = useContext(AuthContext);
  const { t, locale } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: ''
  });
  const [profilePicture, setProfilePicture] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        email: user.email || ''
      });
      if (user.profile_picture) {
        setPreviewUrl(user.profile_picture);
      }
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        notify(t('File size must be less than 5MB'), 'warning');
        return;
      }

      if (!file.type.startsWith('image/')) {
        notify(t('Please select an image file'), 'warning');
        return;
      }

      setProfilePicture(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone
      };

      // If there's a new profile picture, convert to base64
      if (profilePicture) {
        updateData.profile_picture = previewUrl;
      }

      const response = await userAPI.updateProfile(updateData);
      
      // Update context
      updateUser(response.data.user);
      
      notify(t('Profile updated successfully!'), 'success');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      notify(error.response?.data?.message || t('Error updating profile'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  return (
    <div className="profile-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('My Profile')}</h1>
          <p className="page-sub">{t('Manage your personal information')}</p>
        </div>
      </div>

      <div className="pf-content">
        {/* Profile Card */}
        <div className="card pf-card">
          <div className="pf-picture">
            <div className="pf-avatar">
              {previewUrl ? (
                <img src={previewUrl} alt="Profile" />
              ) : (
                getInitials(user?.first_name, user?.last_name)
              )}
            </div>
            
            {isEditing && (
              <div className="pf-upload">
                <label htmlFor="profile-upload" className="btn btn--secondary pf-upload__btn">
                  <Camera aria-hidden="true" /> {t('Change Photo')}
                </label>
                <input
                  id="profile-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
                <p className="field-hint">{t('Max size: 5MB')}</p>
              </div>
            )}
          </div>

          <div className="pf-info">
            <form onSubmit={handleSubmit}>
              <div className="pf-form-row">
                <div className="pf-field">
                  <label className="field-label">{t('First Name')}</label>
                  <input className="input"
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    required
                  />
                </div>

                <div className="pf-field">
                  <label className="field-label">{t('Last Name')}</label>
                  <input className="input"
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    required
                  />
                </div>

                <div className="pf-field">
                  <label className="field-label">{t('Email')}</label>
                  <input
                    type="email"
                    value={formData.email}
                    disabled
                    className="input"
                  />
                  <small className="field-hint">{t('Email cannot be changed')}</small>
                </div>

                <div className="pf-field">
                  <label className="field-label">{t('Phone')}</label>
                  <input className="input"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="555-0123"
                  />
                </div>

                <div className="pf-field">
                  <label className="field-label">{t('Role')}</label>
                  <div className="status status--neutral pf-role">
                    {user?.role?.replace('_', ' ')}
                  </div>
                </div>

                <div className="pf-field">
                  <label className="field-label">{t('Last Login')}</label>
                  <input
                    type="text"
                    value={user?.last_login ? new Date(user.last_login).toLocaleString(locale) : t('N/A')}
                    disabled
                    className="input"
                  />
                </div>
              </div>

              <div className="pf-form-actions">
                {!isEditing ? (
                  <button 
                    type="button" 
                    onClick={() => setIsEditing(true)}
                    className="btn btn--primary"
                  >
                    <Pencil aria-hidden="true" /> {t('Edit Profile')}
                  </button>
                ) : (
                  <>
                    <button 
                      type="button" 
                      onClick={() => {
                        setIsEditing(false);
                        setProfilePicture(null);
                        setPreviewUrl(user?.profile_picture || null);
                      }}
                      className="btn btn--secondary"
                    >
                      {t('Cancel')}
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn--primary"
                      disabled={loading}
                    >
                      {loading ? t('Saving...') : <><Save aria-hidden="true" /> {t('Save Changes')}</>}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;